import { GAME_EVENTS } from "../../../constants/game";
import { SOCKET_EVENTS } from "../../../socket/Events";
import { SCENE_EVENTS } from "../../MVC/Model";
import { WebRTCAdaptor } from "../../WebRTC/media-server-external/webrtc_adaptor";
import ChessModeWindow from "./ChessModeWindow";
import VolumeAnalyser from "../AudioAnalyser/VolumeAnalyser";

export default class ChessModeController {
  constructor({ model, socket }) {
    this.connectPromise = null;
    this.connectResolve = null;
    this.connectReject = null;
    window.chessModeController = this;
    this.userVideoId = "chess-player-video";
    this.visitorSeat = 1;

    this.model = model;
    this.socket = socket;
    this.chessModeView = new ChessModeWindow({ model, socket, controller: this });

    this.webRTCAdaptor = null;
    this.dominantSpeakerFinderId = null;
    this.checkSpeakingStreamIds = new Array();

    this.audioAnalyzers = {}

    this.sdpConstraints = {
      OfferToReceiveAudio: false,
      OfferToReceiveVideo: false
    };

    this.mediaConstraints = {
      video: {
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: { ideal: 30, max: 30 }, // Lower frame rate to reduce load
      },
      audio: true
    };

    this.pc_config = {
      'iceServers': [{
        'urls': 'stun:stun1.l.google.com:19302'
      }]
    };

    this.roomNameBox = "chessID";
    this.roomOfStream = new Array();
    this.streamsList = new Array();

    this.publishStreamId;
    this.isDataChannelOpen = false;
    this.isMicMuted = false;
    this.isCameraOff = false;
    this.roomTimerId = -1;
    this.isPlayer = false;
    this.gameLocked = false;

    // Event Listeners
    this.model.addEventListener(SOCKET_EVENTS.DISCONNECT, () => {
      try {
        this.leaveRoom();
        this.webRTCAdaptor?.closeStream()
      } catch (e) {
        console.error(e);
      }
    });

    this.model.addEventListener(SCENE_EVENTS.DEVICE_SETTINGS_CHANGED, (e) => {
      if (this.publishStreamId) {
        const cameraId = this.model.view.deviceSettingsMenu.selected.camera.value;
        this.webRTCAdaptor.switchVideoCameraCapture(this.publishStreamId, cameraId)
      }
    })
  }

  connect = () => {
    // If we already have a connection, clean it up first
    if (this.connectPromise) return this.connectPromise;
    this.connectPromise = new Promise((resolve, reject) => {
      this.connectResolve = resolve;
      this.connectReject = reject;

      this.webRTCAdaptor = new WebRTCAdaptor({
        websocket_url: "wss://bagless-media-server.com:5443/ChessGameApp/websocket",
        mediaConstraints: this.model.getMediaConstraints() || this.mediaConstraints,
        peerconnection_config: this.pc_config,
        sdp_constraints: this.sdpConstraints,
        localVideoId: this.userVideoId,
        isPlayMode: true,
        debug: false,
        callback: (info, obj) => {
          if (info === "initialized") {
            if (!this.initialised) {
              this.joinRoom();
              this.initialised = true;
            }
          } else if (info === "joinedTheRoom") {
            this.roomJoined = true;
            this.publishStreamId = obj.streamId;
            this.streamsList = obj.streams;
            this.model.publishStreamId = this.publishStreamId;
            this.publish();
            obj.streams.forEach(stream => {
              this.webRTCAdaptor.play(stream, null, this.roomNameBox);
            });

            this.roomTimerId = setInterval(() => {
              this.webRTCAdaptor.getRoomInfo(this.roomNameBox, this.publishStreamId);
            }, 3000);
            resolve();
            this.model.dispatchEvent({ type: GAME_EVENTS.CHESS_CONTROLLER_CONNECTED });
            if (this.isPlayer) {
              this.model.dispatchEvent({ type: SCENE_EVENTS.SHOW_CHESS_PLAYER_MODE_MENU });
            } else {
              this.model.dispatchEvent({ type: SCENE_EVENTS.SHOW_CHESS_SPECTATOR_MODE_MENU });
            }

          } else if (info === "newStreamAvailable") {
            if (!this.roomJoined) return;
            this.playVideo(obj);
          } else if (info === "leavedFromRoom") {
            clearInterval(this.roomTimerId);
            this.streamsList.forEach(stream => this.removeRemoteVideo(stream));
            this.streamsList = [];
            this.webRTCAdaptor.closeWebSocket();
            this.model.dispatchEvent({ type: SCENE_EVENTS.HIDE_CHESS_MENU });
            this.model.dispatchEvent({ type: GAME_EVENTS.CHESS_CONTROLLER_ROOM_LEFT });
          } else if (info === "closed") {
            reject(new Error("Connection closed"));
          } else if (info === "roomInformation") {
            // Store old streams for comparison
            const oldStreams = [...this.streamsList];
            // Update streamsList first to keep it in sync
            this.streamsList = obj.streams;

            // Play only genuinely new streams
            for (let str of obj.streams) {
              const videoExists = document.getElementById(`chessRemoteVideo-${str}`);
              if (!oldStreams.includes(str) && !videoExists) {
                this.webRTCAdaptor.play(str, null, this.roomNameBox);
              }
            }

            // Remove streams no longer present
            for (let str of oldStreams) {
              if (!obj.streams.includes(str)) {
                this.removeRemoteVideo(str);
              }
            }

            this.checkVideoTrackStatus(this.streamsList);
          }
        },
        callbackError: (error, message) => {
          reject(new Error(message || error));
        }
      });
    });

    return this.connectPromise;
  };

  joinRoom = () => {
    this.roomJoined = true;
    this.publishStreamId = `CHESS-${this.isPlayer ? 'PLAYER' : 'WATCHER'}-${this.model.config.userId}`;
    this.webRTCAdaptor.joinRoom(this.roomNameBox, this.publishStreamId);
  };

  leaveRoom = () => {
    if (!this.roomJoined) return;
    this.roomJoined = false;
    this.isPlayer = false;
    if (this.webRTCAdaptor) {
      this.webRTCAdaptor.leaveFromRoom(this.roomNameBox);
      this.webRTCAdaptor.closeStream()
    }

    const userVideo = document.getElementById(this.userVideoId)
    if (userVideo?.srcObject) {
      userVideo.srcObject.getTracks().forEach(track => track.stop());
      userVideo.srcObject = null;
    }

    Object.keys(this.audioAnalyzers).forEach(key => {
      const audioAnalyzer = this.audioAnalyzers[key]
      audioAnalyzer.destroy()
      this.model.removeAudioAnalyzer(audioAnalyzer)
      delete this.audioAnalyzers[key]
    })

    this.model?.view?.handleAudioOn()
    this.model?.view?.handleVideoStreamOn()
  };

  publish = () => {
    this.publishing = true;
    this.webRTCAdaptor.publish(this.publishStreamId);
  };

  unPublish = () => {
    this.publishing = false;
    if (this.webRTCAdaptor && this.publishStreamId) {
      this.webRTCAdaptor.stop(this.publishStreamId);
    }
  };

  playVideo = (obj) => {
    const videoId = `chessRemoteVideo-${obj.streamId}`;
    if (!document.getElementById(videoId)) {
      this.createRemoteVideo(obj.streamId, obj.stream);
    }
  };

  createRemoteVideo = (streamId, stream) => {
    const isPlayer = streamId.includes('CHESS-PLAYER');
    const isWatcher = streamId.includes('CHESS-WATCHER');

    let userId = streamId;
    if (isPlayer) {
      userId = streamId.replace('CHESS-PLAYER-', '');
    } else if (isWatcher) {
      userId = streamId.replace('CHESS-WATCHER-', '');
    }

    const player = document.createElement("div");
    player.id = `player-${streamId}`;
    player.innerHTML = `<video id="chessRemoteVideo-${streamId}" autoplay playsinline></video>`;
    document.getElementById("players").appendChild(player);
    player.querySelector("video").srcObject = stream;
    if (isPlayer) {
      this.model.dispatchEvent({ type: GAME_EVENTS.NEW_PLAYER_VIDEO, data: { playerId: userId, streamId: streamId } });
    } else {
      this.model.dispatchEvent({ type: GAME_EVENTS.NEW_VISITOR_VIDEO, data: { playerId: userId, streamId: streamId } });
    }

    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    const volumeAnalyser = new VolumeAnalyser()
      .setAudioContext(this.audioContext)
      .setStream(stream)
      .setEnabled(false)
    volumeAnalyser.videoElement = player.querySelector("video");
    $(player.querySelector("video")).data('volumeAnalyser', volumeAnalyser);

    this.audioAnalyzers[stream.id] = volumeAnalyser;
    this.model.registerAudioAnalyzer(volumeAnalyser);
  };

  removeRemoteVideo = (streamId) => {
    const videoId = `chessRemoteVideo-${streamId}`;
    const video = document.getElementById(videoId);
    if (video) {
      if (video.srcObject) {
        video.srcObject.getTracks().forEach(track => track.stop());
        video.srcObject = null;
      }
      const playerElement = document.getElementById(`player-${streamId}`);
      if (playerElement) {
        const volumeAnalyser = $(playerElement).data('volumeAnalyser');
        if (volumeAnalyser) {
          volumeAnalyser.destroy();
          this.model.removeAudioAnalyzer(volumeAnalyser);
        }

        playerElement.remove();
      }
    }
    // Note: No need to filter here since streamsList is updated in roomInformation
  };

  muteLocalMic = () => {
    if (this.webRTCAdaptor) {
      this.isMicMuted = true;
      this.webRTCAdaptor.muteLocalMic();
      this.socket.updateUserData({ 'userAudioMuted': true });
    }
  };

  unmuteLocalMic = () => {
    if (this.webRTCAdaptor) {
      this.isMicMuted = false;
      this.webRTCAdaptor.unmuteLocalMic();
      this.socket.updateUserData({ 'userAudioMuted': false });
      const element = $("#chess-player-video");
    }
  };

  turnOffLocalCamera = () => {
    if (this.webRTCAdaptor && !this.isCameraOff) {
      this.isCameraOff = true;
      this.webRTCAdaptor.turnOffLocalCamera(this.publishStreamId);
      this.socket.updateUserData({ 'userVideoMuted': true });
    }
  };

  turnOnLocalCamera = () => {
    if (this.webRTCAdaptor && this.isCameraOff) {
      this.isCameraOff = false;
      this.webRTCAdaptor.turnOnLocalCamera(this.publishStreamId);
      this.socket.updateUserData({ 'userVideoMuted': false });
    }
  };

  checkVideoTrackStatus = (streamsList) => {
    streamsList.forEach((item) => {
      if (item === this.publishStreamId) return; // Skip own stream
      const videoId = `chessRemoteVideo-${item}`;
      const video = document.getElementById(videoId);
      if (video && video.srcObject && !video.srcObject.active) {
        this.webRTCAdaptor.stop(item);
        this.webRTCAdaptor.play(item, null, this.roomNameBox);
      } else if (!video) {
        this.webRTCAdaptor.play(item, null, this.roomNameBox);
      }
    });
  }
}
